import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Spin } from "antd";
import HomeLayout from "../layouts/HomeLayout";
import MetaTags from "./MetaTags";
import styled from "styled-components";
import { colors, styles } from "../utils/theme";
import { device } from "../utils/breakpoints";
import { BannerTitle, HeaderTitle } from "../styles/sharedStyles";
import unwrapImages from "rehype-unwrap-images";

// Simple frontmatter parser (browser-friendly)
const parseFrontmatter = (content) => {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);

  if (!match) {
    return { data: {}, content };
  }

  const [, frontmatterStr, mdxContent] = match;
  const data = {};

  // Parse YAML-like frontmatter
  frontmatterStr.split("\n").forEach((line) => {
    const colonIndex = line.indexOf(":");
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();

      // Remove quotes
      if (
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1);
      }

      // Parse boolean
      if (value === "true") value = true;
      if (value === "false") value = false;

      data[key] = value;
    }
  });

  return { data, content: mdxContent };
};

const LandingPageRenderer = ({ slug: propSlug }) => {
  const { slug: paramSlug } = useParams();
  const navigate = useNavigate();
  const slug = propSlug || paramSlug;

  const [markdownContent, setMarkdownContent] = useState("");
  const [frontmatter, setFrontmatter] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMarkdown = async () => {
      if (!slug) {
        navigate("/");
        return;
      }

      try {
        setLoading(true);

        // Laad markdown file als raw text
        const response = await fetch(`./landing-pages/${slug}.md`);

        if (!response.ok) {
          // Redirect naar home bij niet-bestaande pagina
          navigate("/");
          return;
        }

        // Check of we HTML krijgen in plaats van markdown (CRA fallback bug)
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("text/html")) {
          // HTML response betekent dat het bestand niet bestaat (CRA fallback)
          navigate("/");
          return;
        }

        const rawContent = await response.text();

        // Parse frontmatter
        const { data: metadata, content: markdown } =
          parseFrontmatter(rawContent);
        setFrontmatter(metadata);
        setMarkdownContent(markdown);
      } catch (err) {
        console.error("Error loading landing page:", err);
        // Redirect naar home bij error
        navigate("/");
        return;
      } finally {
        setLoading(false);
      }
    };

    loadMarkdown();
  }, [slug, navigate]);

  // Custom renderers voor basis HTML elementen
  const markdownComponents = {
    h1: ({ children }) => <HeaderTitle>{children}</HeaderTitle>,
    h2: ({ children }) => <BannerTitle>{children}</BannerTitle>,
    a: ({ children, href }) => {
      if (href.includes("https")) {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      } else {
        return <Link to={href}>{children}</Link>;
      }
    },
  };

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "2rem" }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <HomeLayout hasGradientBg={false}>
      <MetaTags
        title={frontmatter.title}
        description={frontmatter.description}
        keywords={frontmatter.keywords || ""}
        path={`/${slug}`}
      />

      <Wrapper className="container">
        <ReactMarkdown
          components={markdownComponents}
          rehypePlugins={[unwrapImages]}
        >
          {markdownContent}
        </ReactMarkdown>
      </Wrapper>
    </HomeLayout>
  );
};

const Wrapper = styled.article`
  margin-block: 3rem;

  font-size: 16px;
  line-height: 24px;

  @media ${device.lg} {
    font-size: 20px;
    line-height: 30px;
  }

  h1 {
    margin-bottom: 0.75em;
  }

  h2 {
    font-size: 1.25em;
    margin-top: 1.5em;
  }

  h3 {
    font-weight: 700;
    font-size: 1em;
    margin-top: 1.5em;
  }

  ul,
  ol {
    margin-bottom: 1em;
  }

  img {
    display: block;
    border-radius: ${styles.card_border_radius};
    aspect-ratio: 4/3;
    object-fit: cover;
    width: min(600px, 100%);
    margin-block: 1rem;
  }

  // Styles only for hero images
  h1 + img {
    width: 100%;
    aspect-ratio: 2/1;
    box-shadow: ${styles.image_box_shadow};
    object-fit: cover;
    margin-bottom: 2rem;
  }

  a {
    color: ${colors.secondary};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

export default LandingPageRenderer;
